import React, { useState } from 'react';
import './RainCloudV2.css';
import { typeFlowerColors } from '../constants/constants';

export function RainCloudV2({ tutorData }) {
  const [hoveredCloud, setHoveredCloud] = useState(null);

  if (!tutorData || !tutorData.processedData) {
    return null;
  }

  const { processedData } = tutorData;

  // Filter out zero counts and sort by count (descending)
  const activeTalkMoves = Object.entries(processedData)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  if (activeTalkMoves.length === 0) {
    return null;
  }

  // Calculate sizes
  const maxValue = Math.max(...activeTalkMoves.map(([_, count]) => count));
  const minScale = 0.7;
  const maxScale = 1.0;

  const getCloudScale = (count) => {
    if (maxValue === 0) return minScale;
    const scale = count / maxValue;
    return minScale + (maxScale - minScale) * scale;
  };

  // Get color for talk move type
  const getColor = (type) => {
    const colorClass = typeFlowerColors[type] || 'whiteflower';
    const colorMap = {
      'redflower': '#f07d7f',
      'greenflower': '#aae76d',
      'blueflower': '#79aaee',
      'yellowflower': '#eed571',
      'purpleflower': '#c26be4',
      'blackflower': '#333',
      'whiteflower': '#e0e0e0'
    };
    return colorMap[colorClass] || '#e0e0e0';
  };

  // Format talk move type name for display
  const formatTypeName = (type) => {
    return type
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/^./, str => str.toUpperCase());
  };

  // Calculate number of raindrops based on count
  const getNumDrops = (count) => {
    return Math.min(Math.ceil(count / 2), 10); // Max 10 drops per cloud
  };

  return (
    <div className="rain-cloud-container" role="img" aria-label="Tutor talk moves visualization">
      <div className="rain-clouds-wrapper">
        {activeTalkMoves.slice(0, 3).map(([type, count], index) => {
          const scale = getCloudScale(count);
          const color = getColor(type);
          const numDrops = getNumDrops(count);

          return (
            <div
              key={type}
              className="rain-cloud-item"
              style={{
                animationDelay: `${index * 0.2}s`
              }}
              onMouseEnter={() => setHoveredCloud(index)}
              onMouseLeave={() => setHoveredCloud(null)}
            >
              <div
                className="rain-cloud-wrapper"
                style={{
                  transform: `scale(${scale})`,
                }}
              >

                <svg
                  className="rain-cloud-svg"
                  viewBox="0 0 140 90"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Main cloud */}
                  <path
                    d="M42 64c-12 0-22-9-22-20 0-9 6-17 15-19 2-12 13-21 26-21 10 0 19 5 23 13 3-1 7-2 10-2 14 0 25 11 25 24s-11 25-25 25H42z"
                    fill={color}
                    opacity="0.9"
                  />

                  {/* Soft highlight */}
                  <path
                    d="M60 25c-12 0-21 7-22 16 3-6 11-10 20-10 9 0 16 4 19 10 1-8-6-16-17-16z"
                    fill="#ffffff"
                    opacity="0.25"
                  />

                  {/* Bottom shadow */}
                  <path
                    d="M40 64h66c4-1 8-3 10-6-4 9-13 14-23 14H52c-10 0-19-5-23-14 2 3 6 5 11 6z"
                    fill="#000"
                    opacity="0.12"
                  />
                </svg>


                {/* Tooltip */}
                {hoveredCloud === index && (
                  <div className="rain-cloud-tooltip">
                    <strong>{formatTypeName(type)}</strong>
                    <span>{count}</span>
                  </div>
                )}

                {/* Raindrops falling from cloud */}
                <div className="raindrops-container">
                  {[...Array(numDrops)].map((_, i) => (
                    <div
                      key={i}
                      className="raindrop"
                      style={{
                        backgroundColor: color,
                        left: `${15 + (i % 7) * 12}px`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${1.5 + (i % 3) * 0.3}s`
                      }}
                    />
                  ))}
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RainCloudV2;